import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Heart, ArrowRight, Crown, AlertTriangle, X, Check, RotateCw, Camera, Stethoscope, MapPinned } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n';
import { api, pictureUrl, fileUrl } from '../lib/api';
import { greetingKey, todayString } from '../lib/utils';
import WeatherWidget from '../components/WeatherWidget';
import LanguageSwitcher from '../components/LanguageSwitcher';
import PremiumFromPrice from '../components/PremiumFromPrice';
import LocationOnboardModal from '../components/LocationOnboardModal';
import InstallPWAButton from '../components/InstallPWAButton';
import SowNowWidget from '../components/SowNowWidget';
import AppContent from '../components/AppContent';
import WebAdSlot from '../components/WebAdSlot';
import AddPlantModal from '../components/AddPlantModal';
import SkyScene from '../components/living/SkyScene';
import Tilt from '../components/living/Tilt';
import LivingEmoji from '../components/living/LivingEmoji';
import LivingImage from '../components/living/LivingImage';
import CountUp from '../components/living/CountUp';
import ProgressRing from '../components/living/ProgressRing';
import SpeciesIcon from '../components/species/SpeciesIcon';
import { matchCatalogId } from '../lib/species';
import { PLANT_CATALOG } from '../lib/data';
import { useTheme } from '../contexts/ThemeContext';
import { SNAP_FIND_ROUTE } from '../lib/navigation';
import { navigationStyle } from '../lib/navigationIdentity';
import SnapShortcutControls from '../components/SnapShortcutControls';
import './Dashboard.css';

// Shown while a number is not known yet, so the page never claims "0".
const UNKNOWN = '—';

const COPY = {
    da: { title: 'Lidt omsorg. Mere have.', intro: 'Et lille skridt ad gangen, i dit eget tempo.', next: 'Næste opgave', openTasks: 'Se dagens opgaver', openGarden: 'Åbn din have', tools: 'Hvad vil du gøre?', taskHint: 'Dit overblik i dag', doctorHint: 'Spørg om dine planter', recipeHint: 'Fra have til køkken', edenHint: 'Besøg din byhave', complete: 'Dagens opgaver er klaret.', explore: 'Mere at udforske', loadError: 'Noget af dit overblik kunne ikke opdateres. Prøv igen.', taskError: 'Opgaven kunne ikke gemmes. Prøv igen.', generationError: 'Automatiske vandingsopgaver kunne ikke opdateres.', retry: 'Prøv igen', gardener: 'gartner', account: 'Din konto' },
    en: { title: 'A little care. A growing garden.', intro: 'One small step at a time, at your own pace.', next: 'Next task', openTasks: 'See today’s tasks', openGarden: 'Open your garden', tools: 'What would you like to do?', taskHint: 'Your day at a glance', doctorHint: 'Ask about your plants', recipeHint: 'From garden to kitchen', edenHint: 'Visit your city garden', complete: 'Today’s tasks are all done.', explore: 'More to explore', loadError: 'Part of your overview could not be refreshed. Please try again.', taskError: 'Your task could not be saved. Please try again.', generationError: 'Automatic watering tasks could not be refreshed.', retry: 'Try again', gardener: 'gardener', account: 'Your account' },
    de: { title: 'Ein wenig Pflege. Mehr Garten.', intro: 'Ein kleiner Schritt nach dem anderen, in deinem Tempo.', next: 'Nächste Aufgabe', openTasks: 'Heutige Aufgaben ansehen', openGarden: 'Deinen Garten öffnen', tools: 'Was möchtest du tun?', taskHint: 'Dein Tag im Überblick', doctorHint: 'Fragen zu deinen Pflanzen', recipeHint: 'Vom Garten in die Küche', edenHint: 'Besuche deinen Stadtgarten', complete: 'Die heutigen Aufgaben sind erledigt.', explore: 'Mehr entdecken', loadError: 'Ein Teil deiner Übersicht konnte nicht aktualisiert werden. Bitte versuche es erneut.', taskError: 'Die Aufgabe konnte nicht gespeichert werden. Bitte versuche es erneut.', generationError: 'Automatische Gießaufgaben konnten nicht aktualisiert werden.', retry: 'Erneut versuchen', gardener: 'Gärtner', account: 'Dein Konto' },
    sv: { title: 'Lite omsorg. Mer trädgård.', intro: 'Ett litet steg i taget, i din egen takt.', next: 'Nästa uppgift', openTasks: 'Se dagens uppgifter', openGarden: 'Öppna din trädgård', tools: 'Vad vill du göra?', taskHint: 'Din dag i korthet', doctorHint: 'Fråga om dina växter', recipeHint: 'Från trädgård till kök', edenHint: 'Besök din stadsträdgård', complete: 'Dagens uppgifter är klara.', explore: 'Mer att upptäcka', loadError: 'En del av din översikt kunde inte uppdateras. Försök igen.', taskError: 'Uppgiften kunde inte sparas. Försök igen.', generationError: 'Automatiska vattningsuppgifter kunde inte uppdateras.', retry: 'Försök igen', gardener: 'trädgårdsmästare', account: 'Ditt konto' },
    no: { title: 'Litt omsorg. Mer hage.', intro: 'Ett lite skritt om gangen, i ditt eget tempo.', next: 'Neste oppgave', openTasks: 'Se dagens oppgaver', openGarden: 'Åpne hagen din', tools: 'Hva vil du gjøre?', taskHint: 'Dagen din i korte trekk', doctorHint: 'Spør om plantene dine', recipeHint: 'Fra hage til kjøkken', edenHint: 'Besøk byhagen din', complete: 'Dagens oppgaver er ferdige.', explore: 'Mer å utforske', loadError: 'En del av oversikten kunne ikke oppdateres. Prøv igjen.', taskError: 'Oppgaven kunne ikke lagres. Prøv igjen.', generationError: 'Automatiske vanningsoppgaver kunne ikke oppdateres.', retry: 'Prøv igjen', gardener: 'gartner', account: 'Kontoen din' },
};

export default function Dashboard() {
    const { user } = useAuth();
    const { t, locale } = useI18n();
    const navigate = useNavigate();
    const { experience } = useTheme();
    const copy = COPY[locale] || COPY.en;
    const full = experience === 'full';
    const [tasks, setTasks] = useState([]);
    const [plants, setPlants] = useState([]);
    const [frost, setFrost] = useState(null);
    const [frostDismissed, setFrostDismissed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState([]);
    const [ready, setReady] = useState({ tasks: false, plants: false });
    const [adding, setAdding] = useState(false);
    const [changingTask, setChangingTask] = useState(null);
    const [taskError, setTaskError] = useState(false);
    const requestId = useRef(0);

    const load = useCallback(async () => {
        const request = ++requestId.current;
        setLoading(true);
        // Match the UTC date used by the existing task-generation endpoint.
        const today = new Date().toISOString().slice(0, 10);
        let generationFailed = false;
        const taskRequest = async () => {
            try { await api.post('/tasks/auto-generate'); } catch { generationFailed = true; }
            const response = await api.get('/tasks', { params: { date: today } });
            if (!Array.isArray(response.data)) throw new Error('Invalid task list');
            return response.data;
        };
        const plantRequest = async () => {
            const response = await api.get('/plants');
            if (!Array.isArray(response.data)) throw new Error('Invalid plant list');
            return response.data;
        };
        const lat = user?.location?.lat ?? 55.6761;
        const lon = user?.location?.lon ?? 12.5683;
        const [taskResult, plantResult, weatherResult] = await Promise.allSettled([
            taskRequest(), plantRequest(), api.get('/weather', { params: { lat, lon } }),
        ]);
        if (request !== requestId.current) return;
        const failed = [];
        if (taskResult.status === 'fulfilled') setTasks(taskResult.value);
        else failed.push('tasks');
        if (plantResult.status === 'fulfilled') setPlants(plantResult.value);
        else failed.push('plants');
        if (generationFailed) failed.push('generation');
        setReady({ tasks: taskResult.status === 'fulfilled', plants: plantResult.status === 'fulfilled' });
        if (weatherResult.status === 'fulfilled') {
            const daily = weatherResult.value.data?.daily;
            const minima = Array.isArray(daily?.temperature_2m_min) ? daily.temperature_2m_min : [];
            const idx = minima.findIndex((temperature, index) => index <= 2 && Number.isFinite(temperature) && temperature < 2);
            setFrost(idx < 0 ? null : { idx, min: minima[idx] });
        } else {
            failed.push('weather');
            setFrost(null);
        }
        setErrors(failed);
        setLoading(false);
    }, [user?.location?.lat, user?.location?.lon]);

    useEffect(() => {
        load();
        return () => { requestId.current += 1; };
    }, [load]);

    const toggleTask = async (task) => {
        setChangingTask(task.id);
        setTaskError(false);
        try {
            await api.patch('/tasks/' + task.id, { done: !task.done });
            await load();
        } catch { setTaskError(true); }
        finally { setChangingTask(null); }
    };

    const done = tasks.filter((task) => task.done).length;
    const pending = tasks.filter((task) => !task.done);
    const nextTask = ready.tasks ? pending[0] : null;
    const measured = plants.filter((plant) => Number.isFinite(plant.health));
    const avgHealth = measured.length ? Math.round(measured.reduce((sum, plant) => sum + plant.health, 0) / measured.length) : null;
    const firstName = (user?.name || copy.gardener).split(' ')[0];
    const hasEmptyGarden = ready.plants && !plants.length;
    const heroAction = () => nextTask ? navigate('/tasks') : hasEmptyGarden ? setAdding(true) : navigate('/my-garden');
    const heroActionLabel = nextTask ? copy.openTasks : hasEmptyGarden ? t('garden.add') : copy.openGarden;
    const visibleTasks = [...pending, ...tasks.filter((task) => task.done)].slice(0, full ? 5 : 3);
    const taskPct = tasks.length ? (done / tasks.length) * 100 : 0;

    return (
        <div className={'px-5 pt-10 pb-32 max-w-2xl mx-auto stagger ' + (full ? 'garden-home-full' : 'garden-home-simple')}>
            <header className="relative z-[70] flex items-center justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">{todayString(locale)}</p>
                    <h1 className="font-serif text-3xl sm:text-4xl text-brand-ink leading-tight mt-1">
                        {t(greetingKey())}, <span className="text-brand-green">{firstName}</span>
                    </h1>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    <LanguageSwitcher variant="inline" />
                    <button type="button" onClick={() => navigate('/profile')} aria-label={copy.account} className="avatar-alive rounded-full">
                        {user?.picture ? (
                            <img src={pictureUrl(user.picture)} alt="" className="w-11 h-11 rounded-full border-2 border-brand-bg object-cover" />
                        ) : (
                            <span className="w-11 h-11 rounded-full bg-brand-bg border-2 border-brand-bg flex items-center justify-center text-brand-green font-serif">{firstName[0]?.toUpperCase()}</span>
                        )}
                    </button>
                </div>
            </header>

            <div className="mt-5">
                <SkyScene plants={plants} />
            </div>

            <section className="mt-5 bg-brand-card border border-brand-border rounded-3xl p-5 flex items-end justify-between gap-4" aria-labelledby="garden-home-title">
                <div className="min-w-0">
                    <h2 id="garden-home-title" className="text-[10px] uppercase tracking-widest font-semibold text-brand-green">{copy.title}</h2>
                    <div className="garden-home-next mt-2">
                        {loading && !ready.tasks ? <p role="status">{t('common.loading')}</p> : nextTask ? <>
                            <span>{copy.next}</span>
                            <div className="font-serif text-xl text-brand-ink leading-snug break-words">{nextTask.title}</div>
                        </> : <p>{ready.tasks && tasks.length > 0 && !pending.length ? copy.complete : copy.intro}</p>}
                    </div>
                </div>
                <button type="button" data-testid="dashboard-next-action" onClick={heroAction}
                    className="shrink-0 inline-flex items-center gap-2 bg-brand-green text-white text-sm font-medium px-4 py-2.5 rounded-full hover:bg-brand-greenDark transition-colors">
                    {heroActionLabel} <ArrowRight size={16} className="arrow-nudge" aria-hidden="true" />
                </button>
            </section>

            <FindAccess member={!!user && !user.is_guest} />

            {user?.is_guest && (
                <div data-testid="guest-banner" className="mt-5 bg-brand-ochre/15 border border-brand-ochre/30 rounded-2xl p-4 flex items-start gap-3">
                    <div className="text-xl" aria-hidden="true">{'\u{1F331}'}</div>
                    <div className="flex-1">
                        <p className="text-sm text-brand-ink font-medium">{t('home.guest.title')}</p>
                        <p className="text-xs text-brand-ink2 mt-0.5">{t('home.guest.sub')}</p>
                    </div>
                    <button type="button" data-testid="btn-banner-register" onClick={() => navigate('/register')} className="bg-brand-green text-white text-xs px-4 py-2 rounded-full font-medium hover:bg-brand-greenDark whitespace-nowrap">
                        {t('home.guest_create')}
                    </button>
                </div>
            )}

            {errors.length > 0 && (
                <div role="alert" className="mt-5 bg-brand-terracotta/10 border border-brand-terracotta/30 rounded-2xl p-4 flex items-start gap-3">
                    <AlertTriangle size={20} className="text-brand-terracotta flex-shrink-0 mt-0.5" strokeWidth={1.8} aria-hidden="true" />
                    <p className="flex-1 text-sm text-brand-ink">{errors.every((error) => error === 'generation') ? copy.generationError : copy.loadError}</p>
                    <button type="button" onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-green hover:text-brand-greenDark disabled:opacity-50 whitespace-nowrap">
                        <RotateCw size={14} aria-hidden="true" />{loading ? t('common.loading') : copy.retry}
                    </button>
                </div>
            )}

            {frost && !frostDismissed && (
                <div data-testid="frost-alert" role="status" className="mt-5 bg-brand-terracotta/10 border border-brand-terracotta/30 rounded-2xl p-4 flex items-start gap-3">
                    <AlertTriangle size={20} className="text-brand-terracotta flex-shrink-0 mt-0.5" strokeWidth={1.8} aria-hidden="true" />
                    <div className="flex-1">
                        <p className="text-sm text-brand-ink font-medium">{t('home.frost.title')}</p>
                        <p className="text-xs text-brand-ink2 mt-0.5">{t(frost.idx === 0 ? 'home.tonight' : frost.idx === 1 ? 'home.tomorrow' : 'home.in2days')} {t('home.frost.expected')} {Math.round(frost.min)}°C {t('home.frost.cover')}</p>
                    </div>
                    <button type="button" data-testid="btn-dismiss-frost" onClick={() => setFrostDismissed(true)} aria-label={t('common.close')} className="text-brand-ink3 hover:text-brand-ink p-1"><X size={16} aria-hidden="true" /></button>
                </div>
            )}

            <div className="mt-6">
                <WeatherWidget />
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
                <Stat
                    label={t('home.tasks')}
                    value={ready.tasks ? <><CountUp value={done} />/{tasks.length}</> : UNKNOWN}
                    pct={ready.tasks ? taskPct : 0}
                    sub={ready.tasks ? <><CountUp value={Math.round(taskPct)} />% {t('task.progress')}</> : null}
                    accent="green"
                    testid="stat-tasks"
                />
                <Stat
                    label={t('home.plants_health')}
                    value={ready.plants && avgHealth !== null ? <><CountUp value={avgHealth} />%</> : UNKNOWN}
                    pct={avgHealth || 0}
                    sub={ready.plants ? `${plants.length} ${plants.length === 1 ? t('home.plant_one') : t('home.plant_many')}` : null}
                    accent="ochre"
                    testid="stat-plants"
                />
            </div>

            {full && <SowNowWidget onAdded={load} />}

            <section className="mt-8" aria-labelledby="garden-home-plants-title">
                <SectionHeader id="garden-home-plants-title" title={t('nav.garden')} onMore={() => navigate('/my-garden')} moreText={t('home.see_all')} />
                {!ready.plants ? (
                    <p role="status" className="bg-brand-card border border-brand-border rounded-2xl p-5 text-center text-sm text-brand-ink3">{loading ? t('common.loading') : t('common.error')}</p>
                ) : plants.length === 0 ? (
                    <button type="button" data-testid="empty-add-plant" onClick={() => setAdding(true)} className="w-full bg-brand-card border border-dashed border-brand-border rounded-3xl p-8 text-center hover:border-brand-green transition-colors">
                        <LivingEmoji className="text-4xl mb-2" seed="empty-garden">{'\u{1F331}'}</LivingEmoji>
                        <p className="text-sm text-brand-ink2">{t('garden.empty')}</p>
                        <p className="text-xs text-brand-green font-medium mt-2">+ {t('garden.add')}</p>
                    </button>
                ) : (
                    <div className="flex gap-3 overflow-x-auto pt-2 pb-3 -mx-5 px-5 snap-x">
                        {plants.slice(0, full ? 6 : 4).map((p) => {
                            const catalogId = matchCatalogId(p, PLANT_CATALOG);
                            return (
                                <Tilt key={p.id} as="button" type="button" onClick={() => navigate('/my-garden')}
                                    className="snap-start min-w-[160px] max-w-[160px] bg-brand-card border border-brand-border rounded-2xl p-3 text-left">
                                    {p.image_path ? (
                                        <LivingImage src={fileUrl(p.image_path)} alt="" className="depth-1 h-20 -mx-1 -mt-1 mb-2 rounded-xl" />
                                    ) : catalogId ? (
                                        <SpeciesIcon kind="plant" id={catalogId} emoji={p.emoji} name={p.name} size={144} height={80} rounded="rounded-xl" className="depth-1 -mx-1 -mt-1 mb-2" />
                                    ) : (
                                        <div className="depth-1 h-20 -mx-1 -mt-1 mb-2 rounded-xl bg-gradient-to-b from-brand-sage/20 to-brand-green/10 flex items-end justify-center pb-2">
                                            <LivingEmoji className="text-4xl" seed={p.id}>{p.emoji || '\u{1F331}'}</LivingEmoji>
                                        </div>
                                    )}
                                    <div className="font-serif text-base truncate text-brand-ink">{p.name}</div>
                                    {p.stage && <div className="text-[10px] uppercase tracking-widest text-brand-ink3 mt-0.5 font-semibold truncate">{p.stage}</div>}
                                    {Number.isFinite(p.health) && (
                                        <div className="mt-2 flex items-center gap-1.5 text-xs text-brand-ink2">
                                            <LivingEmoji kind="pulse" seed={`h-${p.id}`}><Heart size={12} className="text-brand-terracotta fill-brand-terracotta/30" aria-hidden="true" /></LivingEmoji>
                                            <div className="flex-1 h-1 bg-brand-bg2 rounded-full overflow-hidden">
                                                <div className={`bar-alive h-full rounded-full ${p.health > 70 ? 'bg-brand-green' : p.health > 40 ? 'bg-brand-ochre' : 'bg-brand-terracotta'}`} style={{ width: `${p.health}%` }} />
                                            </div>
                                            {p.health}%
                                        </div>
                                    )}
                                </Tilt>
                            );
                        })}
                    </div>
                )}
            </section>

            <section className="mt-8" aria-labelledby="garden-home-tasks-title">
                <SectionHeader id="garden-home-tasks-title" title={t('home.tasks')} onMore={() => navigate('/tasks')} moreText={t('home.see_all')} />
                {taskError && <p role="alert" className="mb-3 text-sm text-brand-terracotta">{copy.taskError}</p>}
                {!ready.tasks ? (
                    <p role="status" className="bg-brand-card border border-brand-border rounded-2xl p-5 text-center text-sm text-brand-ink3">{loading ? t('common.loading') : t('common.error')}</p>
                ) : tasks.length === 0 ? (
                    <div className="bg-brand-card border border-brand-border rounded-2xl p-5 text-center text-sm text-brand-ink3">
                        {t('home.empty_tasks')}
                    </div>
                ) : (
                    <div className="space-y-2">
                        {visibleTasks.map((task) => (
                            <button key={task.id} type="button" role="checkbox" aria-checked={!!task.done}
                                aria-label={t('task.done') + ': ' + task.title} aria-busy={changingTask === task.id}
                                disabled={changingTask !== null} data-testid={'btn-toggle-task-' + task.id}
                                onClick={() => toggleTask(task)}
                                className={'w-full flex items-center gap-3 bg-brand-card border border-brand-border rounded-2xl p-3.5 text-left transition-colors hover:border-brand-green disabled:opacity-60 ' + (task.done ? 'opacity-70' : '')}>
                                <span aria-hidden="true" className={'shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ' + (task.done ? 'bg-brand-green border-brand-green text-white' : 'border-brand-border')}>
                                    {task.done && <Check size={14} />}
                                </span>
                                <span className="flex-1 min-w-0">
                                    <span className={'block text-sm text-brand-ink truncate ' + (task.done ? 'line-through' : '')}>{task.title}</span>
                                    {task.time_of_day && <span className="block text-[11px] text-brand-ink3 mt-0.5">{task.time_of_day}</span>}
                                </span>
                                <span className="text-lg" aria-hidden="true">{task.icon || '\u{1F33F}'}</span>
                            </button>
                        ))}
                    </div>
                )}
            </section>

            <nav aria-label={copy.tools} className="mt-8 grid grid-cols-2 gap-3">
                <ActionCard testid="dashboard-add-plant" emoji={'\u{1F331}'} title={t('garden.add')} hint={t('nav.garden')} onClick={() => setAdding(true)} />
                <ActionCard testid="dashboard-tasks" emoji={'\u{1F4CB}'} title={t('home.tasks')} hint={copy.taskHint} onClick={() => navigate('/tasks')} />
                <ActionCard testid="btn-shed" emoji={'\u{1F6D6}'} title={t('menu.shed')} hint={t('home.card_shed_sub')} onClick={() => navigate('/shed')} />
                {full && <>
                    <ActionCard testid="btn-bed-layout" emoji={'\u{1FAB4}'} title={t('home.card_bed')} hint={t('home.card_bed_sub')} onClick={() => navigate('/bed-layout')} />
                    <ActionCard testid="btn-mushrooms" emoji={'\u{1F344}'} title={t('home.card_mushrooms')} hint={t('home.card_mushrooms_sub')} onClick={() => navigate('/mushrooms')} />
                    <ActionCard testid="btn-wild-guide" emoji={'\u{1F33F}'} title={t('home.card_wild')} hint={t('home.card_wild_sub')} onClick={() => navigate('/foraging')} />
                    <ActionCard testid="btn-marketplace" emoji={'\u{1F6D2}'} title={t('home.card_market')} hint={t('home.card_market_sub')} onClick={() => navigate('/marketplace')} badge={t('home.soon')} />
                </>}
            </nav>

            <button
                type="button"
                data-testid="btn-eden"
                onClick={() => navigate('/eden')}
                className="gradient-alive mt-3 w-full bg-gradient-to-br from-brand-green via-brand-sage to-brand-greenDark text-white p-5 rounded-3xl flex items-center justify-between hover:opacity-95 transition-opacity"
            >
                <span className="text-left">
                    <span className="block text-[10px] uppercase tracking-widest font-semibold text-white/80">Eden</span>
                    <span className="block font-serif text-lg mt-1">{t('home.card_eden')}</span>
                    <span className="block text-xs text-white/80 mt-0.5">{copy.edenHint}</span>
                </span>
                <LivingEmoji kind="bob" className="text-3xl" seed="eden">{'\u{1F333}'}</LivingEmoji>
            </button>

            {full && <>
                <AppContent placement="dashboard" className="mt-5" />
                <div className="mt-5"><InstallPWAButton variant="card" /></div>
            </>}

            {/* Web only: consented, clearly labelled and never shown to Premium. */}
            <WebAdSlot slot={process.env.REACT_APP_ADSENSE_SLOT_DASHBOARD} className="mt-5" />

            {user?.premium && (
                <button
                    type="button"
                    data-testid="btn-weekly-plan"
                    onClick={() => navigate('/weekly-plan')}
                    className="gradient-alive mt-3 w-full bg-gradient-to-br from-brand-green via-brand-sage to-brand-greenDark text-white p-5 rounded-3xl flex items-center justify-between hover:opacity-95"
                >
                    <span className="text-left">
                        <span className="block text-[10px] uppercase tracking-widest font-semibold text-brand-bg/70"><Crown size={11} className="inline -mt-0.5 mr-1" aria-hidden="true" />Premium · AI</span>
                        <span className="block font-serif text-lg mt-1">{t('plan.title')}</span>
                    </span>
                    <ArrowRight size={18} aria-hidden="true" />
                </button>
            )}

            {full && !user?.premium && (
                <button
                    type="button"
                    data-testid="btn-go-premium"
                    onClick={() => navigate('/premium')}
                    className="gradient-alive mt-3 w-full bg-gradient-to-br from-brand-ochre via-brand-terracotta to-brand-ochre text-white p-5 rounded-3xl flex items-center justify-between hover:opacity-95 transition-opacity"
                >
                    <span className="text-left">
                        <span className="block text-[10px] uppercase tracking-widest font-semibold text-white/80"><Crown size={11} className="inline -mt-0.5 mr-1" aria-hidden="true" />Premium</span>
                        <span className="block font-serif text-lg mt-1">{t('premium.upsell_title')}</span>
                        <PremiumFromPrice />
                    </span>
                    <ArrowRight size={18} aria-hidden="true" />
                </button>
            )}

            {adding && <PlantDialog label={t('garden.add')} onClose={() => setAdding(false)} onAdded={load} />}
            <LocationOnboardModal />
        </div>
    );
}

function FindAccess({ member }) {
    const { t } = useI18n();
    const navigate = useNavigate();
    return <section className="dashboard-find-access" aria-label={t('finds.tools_label')}>
        <div className="dashboard-find-pair" data-testid="dashboard-doctor-snap">
            <button type="button" data-testid="btn-ask-doctor" data-nav-function="doctor" style={navigationStyle('/garden-doctor')}
                className="dashboard-find-action" onClick={() => navigate('/garden-doctor')}>
                <Stethoscope size={26} aria-hidden="true" />
                <strong>{t('nav.doctor')}</strong>
                <span>{t('home.ask_doctor')}</span>
            </button>
            <button type="button" data-testid="btn-quickid" data-nav-function="camera" style={navigationStyle(SNAP_FIND_ROUTE)}
                className="dashboard-find-action" aria-describedby="dashboard-snap-safety" onClick={() => navigate(SNAP_FIND_ROUTE)}>
                <Camera size={26} aria-hidden="true" />
                <strong>{t('finds.snap')}</strong>
                <span>{t('finds.snap_hint')}</span>
            </button>
        </div>
        <p id="dashboard-snap-safety" className="dashboard-find-safety">{t('finds.snap_safety')}</p>
        <button type="button" data-testid="dashboard-find-log" data-nav-function="map" style={navigationStyle('/finds-map')}
            className="dashboard-find-log" onClick={() => navigate(member ? '/finds-map' : '/login')}>
            <MapPinned size={22} aria-hidden="true" />
            <span><strong>{t('finds.my_log')}</strong><small>{t('finds.private_log_sub')}</small></span>
            <ArrowRight size={17} aria-hidden="true" />
        </button>
        <SnapShortcutControls prefix="dashboard" />
    </section>;
}

function SectionHeader({ id, title, onMore, moreText }) {
    return (
        <div className="flex items-end justify-between mb-3">
            <h2 id={id} className="font-serif text-2xl text-brand-ink">{title}</h2>
            {onMore && (
                <button type="button" onClick={onMore} className="text-xs text-brand-green hover:text-brand-greenDark font-medium uppercase tracking-widest">
                    {moreText}
                </button>
            )}
        </div>
    );
}

function Stat({ label, value, sub, pct, accent, testid }) {
    const c = accent === 'green' ? 'bg-brand-green/10 text-brand-green' : 'bg-brand-ochre/15 text-brand-ochre';
    const ring = accent === 'green' ? 'var(--brand-green)' : 'var(--brand-ochre)';
    return (
        <Tilt data-testid={testid} className="bg-brand-card border border-brand-border rounded-3xl p-5">
            <div className={`inline-block text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full ${c}`}>{label}</div>
            <div className="mt-3 flex items-center justify-between gap-2">
                <div className="depth-1 min-w-0">
                    <div className="font-serif text-3xl text-brand-ink leading-none tabular-nums">{value}</div>
                    {sub && <div className="text-xs text-brand-ink3 mt-1">{sub}</div>}
                </div>
                <span className="depth-2 shrink-0"><ProgressRing value={pct} size={44} stroke={4} color={ring} /></span>
            </div>
        </Tilt>
    );
}

function ActionCard({ testid, emoji, title, hint, onClick, badge }) {
    return (
        <Tilt as="button" type="button" data-testid={testid} onClick={onClick} className="relative bg-brand-card border border-brand-border rounded-3xl p-4 text-left">
            {badge && <span className="absolute top-3 right-3 text-[8px] uppercase tracking-widest font-semibold bg-brand-ochre/20 text-brand-ochre px-2 py-0.5 rounded-full">{badge}</span>}
            <LivingEmoji kind="bob" className="depth-2 text-2xl mb-1" seed={emoji}>{emoji}</LivingEmoji>
            <span className="depth-1 block font-serif text-base text-brand-ink">{title}</span>
            <span className="block text-xs text-brand-ink3 mt-0.5">{hint}</span>
        </Tilt>
    );
}

function PlantDialog({ label, onClose, onAdded }) {
    const triggerRef = useRef(document.activeElement);
    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = previousOverflow; };
    }, []);
    // Share the existing modal stack so AI confirmation portals stay usable.
    return <DialogPrimitive.Root open onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogPrimitive.Content className="garden-home-add-dialog" aria-describedby={undefined}
            onCloseAutoFocus={(event) => { event.preventDefault(); triggerRef.current?.focus(); }}>
            <DialogPrimitive.Title className="sr-only">{label}</DialogPrimitive.Title>
            <AddPlantModal open onClose={onClose} onAdded={onAdded} />
        </DialogPrimitive.Content>
    </DialogPrimitive.Root>;
}
